import './dateSelector.scss';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { LoadingOverlay } from '@eliberty/design-system-react';
import { addDays, startOfMonth } from 'date-fns';

import type { DisplayMode } from '~atoms';
import { DISPLAY_MODE, SelectorHeader, SelectorLayout } from '~atoms';
import { getLocale } from '~helpers';
import { useSelectorCollapse } from '~hooks';
import { DateSummary, DynamicPriceCalendar, SimpleCalendar, TimeslotCalendar } from '~molecules';
import { Maybe, Product, Timeslot } from '~types/appsync.types';
import {
  computeMinSelectableDate,
  convertExclusionPeriodsToDateIntervals,
  getFirstAvailableDate,
  isSelectable,
  parseCalendarDate,
  parseDateFromUrl,
} from '~utils';

interface DateSelectorProps {
  product?: Maybe<Product>;
  validityCategoryValue?: number;
  selectedDate?: Date;
  selectedTimeslot?: Timeslot;
  disabled?: boolean;
  currency?: string;
  onDateChange?: (date: Date | undefined) => void;
  onTimeslotSelected: (slot: Timeslot | undefined) => void;
  participantsCount?: number;
  includeDates?: Date[];
  /** Callback when selected date has a dynamic price discount */
  onDynamicDiscountChange?: (hasDiscount: boolean) => void;
  /** When true, the calendar will not auto-collapse after selecting a timeslot */
  disableAutoCollapse?: boolean;
  /** When true, hide the calendar and show a fixed date label instead */
  hideCalendar?: boolean;
  displayMode?: DisplayMode;
  forceCollapsed?: boolean;
  onPopoverToggle?: () => void;
  onClickOutside?: () => void;
}

function shouldCollapse(isBooking?: boolean, date?: Date, slot?: Timeslot): boolean {
  return (!!date && !isBooking) || (!!date && !!slot && !!isBooking);
}

/**
 * DateSelector component allows users to select a date with an optional calendar modal.
 * It provides a summary of the selected date and handles date validation.
 */
const DateSelector = ({
  product,
  validityCategoryValue,
  selectedDate,
  selectedTimeslot,
  disabled = false,
  currency = 'EUR',
  onDateChange,
  onTimeslotSelected,
  participantsCount,
  includeDates,
  onDynamicDiscountChange,
  disableAutoCollapse = false,
  hideCalendar = false,
  displayMode = DISPLAY_MODE.COLLAPSE,
  forceCollapsed,
  onPopoverToggle,
  onClickOutside,
}: DateSelectorProps) => {
  const locale = useMemo(() => getLocale(), []);
  const [date, setDate] = useState<Date | undefined>(selectedDate);
  const { isCollapsed, setIsCollapsed, toggle } = useSelectorCollapse({
    displayMode,
    forceCollapsed,
    initialCollapsed: () =>
      disableAutoCollapse ? false : shouldCollapse(product?.isBooking, selectedDate),
    onPopoverToggle,
  });

  const minDate = useMemo(
    () =>
      computeMinSelectableDate(
        product?.firstSellableDate ? parseCalendarDate(product.firstSellableDate) : undefined,
      ),
    [product?.firstSellableDate],
  );
  const maxDate = useMemo(
    () => (product?.lastSellableDate ? parseCalendarDate(product.lastSellableDate) : undefined),
    [product?.lastSellableDate],
  );

  const excludeDateIntervals = useMemo(
    () => (product ? convertExclusionPeriodsToDateIntervals(product.exclusionPeriods) : []),
    [product],
  );

  const [dynamicPricesFromDate, setDynamicPricesFromDate] = useState<Date | null>(() => {
    const urlDate = parseDateFromUrl(new URL(globalThis.location.href).searchParams, new Date());
    if (urlDate) {
      const monthStart = startOfMonth(urlDate);
      return new Date(Math.max(monthStart.getTime(), minDate.getTime()));
    }
    return getFirstAvailableDate({
      excludeDateIntervals,
      maxDate,
      minDate,
    });
  });

  const consumerCategories = useMemo(() => {
    if (!product?.hasDynamicPricing) return [];
    return product.consumerCategories;
  }, [product?.hasDynamicPricing, product?.consumerCategories]);

  useEffect(() => {
    setDate(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    if (displayMode === DISPLAY_MODE.COLLAPSE && !disableAutoCollapse) {
      setIsCollapsed(shouldCollapse(product?.isBooking, date, selectedTimeslot));
    }
  }, [
    date,
    product?.isBooking,
    selectedTimeslot,
    disableAutoCollapse,
    displayMode,
    setIsCollapsed,
  ]);

  useEffect(() => {
    if (displayMode === DISPLAY_MODE.COLLAPSE && product?.hasDynamicPricing) {
      setIsCollapsed(false);
    }
  }, [product, displayMode, setIsCollapsed]);

  const handleDateSelect = (selectedDate?: Date) => {
    setDate(selectedDate);
    onDateChange?.(selectedDate);
  };

  const handleTimeslotSelect = (slot: Timeslot | undefined) => {
    onTimeslotSelected(slot);
  };

  const isSelectableDate = useCallback(
    (d: Date) => isSelectable(d, minDate, maxDate, excludeDateIntervals),
    [minDate, maxDate, excludeDateIntervals],
  );

  const computedEndDate =
    date && validityCategoryValue && validityCategoryValue > 1
      ? addDays(date, validityCategoryValue - 1)
      : undefined;

  // Derived visual state: whether the calendar should appear closed.
  // `isCollapsed` is the raw interactive toggle; `isVisuallyCollapsed` adds business rules.
  const isVisuallyCollapsed =
    !disableAutoCollapse && (!product?.calendarDisplay || (isCollapsed && !!date));

  const renderCalendar = () => {
    if (!product) {
      return null;
    }

    const baseProps = {
      excludeDateIntervals,
      includeDates,
      maxDate,
      minDate,
      rangeDuration: validityCategoryValue,
      selectedDate: date,
    };

    // Product with timeslots
    if (product.isBooking) {
      return (
        <div className='DateSelector_calendarContainer'>
          <TimeslotCalendar
            {...baseProps}
            productSlug={product.slug}
            participantsCount={participantsCount}
            onDateSelect={handleDateSelect}
            onTimeslotSelect={handleTimeslotSelect}
            selectedTimeslot={selectedTimeslot}
            hasDynamicPricing={product.hasDynamicPricing}
            consumerCategories={product.consumerCategories}
            currency={currency}
            hideCalendar={hideCalendar}
          />
        </div>
      );
    }

    // Dynamic pricing without timeslots
    if (product.hasDynamicPricing) {
      return (
        <div className='DateSelector_calendarContainer DateSelector_calendarContainer-dynamicPricing'>
          <DynamicPriceCalendar
            {...baseProps}
            isSelectableDate={isSelectableDate}
            productSlug={product.slug}
            consumerCategories={consumerCategories}
            currency={currency}
            dynamicPricesFromDate={dynamicPricesFromDate}
            onDynamicPricesFromDateChange={setDynamicPricesFromDate}
            openToDate={dynamicPricesFromDate ?? undefined}
            onDateSelect={handleDateSelect}
            onDynamicDiscountChange={onDynamicDiscountChange}
          />
        </div>
      );
    }

    // Simple calendar (no dynamic pricing, no timeslots)
    return (
      <div className='DateSelector_calendarContainer'>
        <SimpleCalendar {...baseProps} onDateSelect={handleDateSelect} />
      </div>
    );
  };

  const calendarContent = renderCalendar();

  const showToggleButton =
    !!date && !!product?.calendarDisplay && !disabled && !disableAutoCollapse && !hideCalendar;

  const selectorHeader = (
    <SelectorHeader
      isClosed={isVisuallyCollapsed}
      isClickable={
        displayMode === DISPLAY_MODE.COLLAPSE &&
        !!(date && isVisuallyCollapsed && !disabled && !disableAutoCollapse)
      }
      onToggle={() => setIsCollapsed(!isCollapsed)}
      onHeaderClick={() => setIsCollapsed(false)}
      showToggleButton={displayMode === DISPLAY_MODE.COLLAPSE && showToggleButton}
      disabled={disabled}
    >
      <div className='DateSelector_title'>
        <div className={`DateSelector_date ${disabled ? 'DateSelector_date-disabled' : ''}`}>
          <FormattedMessage id='date' defaultMessage='Date' />
        </div>

        {!disabled &&
          (displayMode === DISPLAY_MODE.POPOVER || isVisuallyCollapsed) &&
          product &&
          !hideCalendar && (
            <DateSummary
              displayCalendar={product?.calendarDisplay}
              startDate={date}
              endDate={computedEndDate}
              locale={locale}
              timeslot={selectedTimeslot}
            />
          )}

        {hideCalendar && date && (
          <DateSummary
            displayCalendar={true}
            startDate={date}
            endDate={computedEndDate}
            locale={locale}
            timeslot={selectedTimeslot}
          />
        )}
      </div>
    </SelectorHeader>
  );

  const isPopover = displayMode === DISPLAY_MODE.POPOVER;
  const hasCalendar = !!product?.calendarDisplay && !disabled;

  return (
    <div className='DateSelector'>
      {!product && !disabled && (
        <div className='DateSelector DateSelector--loading'>
          <LoadingOverlay visible={true} />
          <SimpleCalendar minDate={minDate} onDateSelect={() => {}} />
        </div>
      )}

      {hasCalendar && (
        <SelectorLayout
          displayMode={displayMode}
          isOpen={isPopover ? !isCollapsed : !isVisuallyCollapsed}
          onToggle={toggle}
          onClickOutside={onClickOutside}
          header={selectorHeader}
          popoverWidth={isPopover ? 450 : undefined}
        >
          {calendarContent}
        </SelectorLayout>
      )}

      {!hasCalendar && isPopover && (
        <SelectorLayout
          displayMode={DISPLAY_MODE.POPOVER}
          isOpen={false}
          onToggle={toggle}
          header={selectorHeader}
        >
          {null}
        </SelectorLayout>
      )}

      {!hasCalendar && !isPopover && selectorHeader}
    </div>
  );
};

export default DateSelector;
